import { useState } from "react";
import Table from "../../Components/Table";
import { Success, Error } from "../../Components/Modal";
import { useForm } from "react-hook-form";
import { Modal } from "react-bootstrap";
import { useMutation } from "react-query";
import useTableControls from "../../Hooks/useTableControls";
import useFetchData from "../../Hooks/useFetchData";
import {
  getNotificationTemplates,
  getNotificationTemplateById,
  editNotificationTemplate,
} from "../../Services/NotificationTemplate";
import { format_date, getSerialNumber } from "../../Utils/helpers";
import PageHeader from "../../Components/PageHeader";
import "../CMSManagement/CMSManagement.css";
import "./NotificationTemplates.css";

const statusOptions = [
  { label: "Active", value: "active" },
  { label: "Inactive", value: "inactive" },
];

const PLACEHOLDERS = [
  "{userName}",
  "{driverName}",
  "{{driver_name}}",
  "{{reason}}",
  "{promoCode}",
  "{discountValue}",
  "{minOrderValue}",
  "{bookingId}",
];

const normalizeTemplateStatus = (value, fallback = "active") => {
  if (value === true || value === 1) return "active";
  if (value === false || value === 0) return "inactive";

  const normalized = String(value ?? "").trim().toLowerCase();
  if (normalized === "active" || normalized === "true") return "active";
  if (normalized === "inactive" || normalized === "false") return "inactive";

  return fallback;
};

const extractTemplateFromResponse = (res) => {
  const raw = res?.data?.data;
  if (!raw) return null;
  if (raw.docs?.length) return raw.docs[0];
  if (raw.data && !raw.title) return raw.data;
  return raw;
};

export default function NotificationTemplates() {
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  const {
    perPage,
    setPerPage,
    status,
    setStatus,
    search_string,
    setSearchString,
  } = useTableControls({ defaultStatus: "" });

  const {
    page,
    setPage,
    isFetching,
    isLoading,
    data,
    refetch,
  } = useFetchData("notification_templates", getNotificationTemplates, [
    perPage,
    search_string,
    status,
  ]);

  const listData = data?.data?.data;
  const templates = listData?.docs || listData?.data || [];

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm();

  const statusValue = watch("status");
  const statusActive = statusValue === "active";

  const { mutate: fetchTemplate, isLoading: loadingTemplate } = useMutation(
    ({ id }) => getNotificationTemplateById(id),
    {
      onSuccess: (res, variables) => {
        const template = extractTemplateFromResponse(res) || variables?.listTemplate;
        if (!template) {
          Error("Template details not found.");
          return;
        }

        setSelectedTemplate(template);
        reset({
          title: template.title || "",
          description: template.description || template.body || "",
          status: normalizeTemplateStatus(
            template.status,
            normalizeTemplateStatus(variables?.listTemplate?.status, "active")
          ),
        });
        setShowEditModal(true);
      },
      onError: (err) => {
        Error(err?.response?.data?.message || "Failed to load template.");
      },
    }
  );

  const { mutate: updateTemplate, isLoading: saving } = useMutation(
    (payload) => editNotificationTemplate(payload),
    {
      onSuccess: (res) => {
        Success(
          "Template Updated",
          res.data?.message || "The notification template has been updated successfully."
        );
        setShowEditModal(false);
        refetch();
      },
      onError: (err) => {
        Error(err?.response?.data?.message || "Failed to update template.");
      },
    }
  );

  const handleEdit = (template) => {
    const id = template?._id || template?.id;
    if (!id) {
      Error("Invalid template id.");
      return;
    }
    fetchTemplate({ id, listTemplate: template });
  };

  const closeModal = () => {
    setShowEditModal(false);
    setSelectedTemplate(null);
  };

  const onSubmit = (formData) => {
    const id = selectedTemplate?._id || selectedTemplate?.id;
    if (!id) {
      Error("Invalid template id.");
      return;
    }

    updateTemplate({
      id,
      title: formData.title,
      description: formData.description,
      status: formData.status,
    });
  };

  const getStatusBadge = (value) => {
    const isActive = normalizeTemplateStatus(value) === "active";

    return (
      <span className={`status-pill ${isActive ? "status-active" : "status-inactive"}`}>
        {isActive ? "Active" : "Inactive"}
      </span>
    );
  };

  const templateKey =
    selectedTemplate?.key ||
    selectedTemplate?.templateKey ||
    selectedTemplate?.slug ||
    null;

  return (
    <div className="app-content content">
      <div className="content-wrapper">
        <div className="content-body">
          <section id="notification-templates">
            <div className="user-details-wrapper">
              <PageHeader
                title="Push Notification Templates"
                subtitle="Manage and edit automated system notification templates"
              />

              <Table
                tableTitles={[
                  "S.No.",
                  "Template Title",
                  "Message Template",
                  "Status",
                  "Last Updated",
                  "Actions",
                ]}
                showEntries
                sortBy
                status_options={statusOptions}
                isLoading={isLoading}
                isFetching={isFetching}
                data={templates}
                totalPages={Math.ceil(listData?.totalPages || 0)}
                totalDocs={listData?.totalDocs || templates.length}
                page={page}
                setPage={setPage}
                setSearchString={setSearchString}
                status={status}
                setStatus={setStatus}
                perPage={perPage}
                setPerPage={setPerPage}
              >
                {templates.map((item, index) => (
                  <tr key={item?._id || item?.id || index}>
                    <td>{getSerialNumber(listData || { page, limit: perPage }, index)}</td>
                    <td className="fw-bold">{item.title || "—"}</td>
                    <td>
                      <p className="nt-message-preview">
                        {item.description || item.body || "—"}
                      </p>
                    </td>
                    <td>{getStatusBadge(item.status)}</td>
                    <td className="text-muted small">
                      {item?.updatedAt
                        ? format_date(item.updatedAt, "MM/DD/YYYY")
                        : item?.createdAt
                          ? format_date(item.createdAt, "MM/DD/YYYY")
                          : "—"}
                    </td>
                    <td>
                      <button
                        type="button"
                        className="nt-edit-btn"
                        onClick={() => handleEdit(item)}
                        disabled={loadingTemplate}
                        title="Edit Template"
                      >
                        <i className="fas fa-pen" />
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </Table>
            </div>
          </section>
        </div>
      </div>

      <Modal
        show={showEditModal}
        onHide={closeModal}
        centered
        size="lg"
        className="cms-modal premium-cms-modal"
        scrollable
      >
        <Modal.Header closeButton className="cms-modal__header">
          <div className="cms-modal__header-main">
            <div className="cms-modal__header-icon">
              <i className="fas fa-bell" />
            </div>
            <div>
              <Modal.Title>Edit Notification Template</Modal.Title>
              <p className="cms-modal__subtitle">
                Update push notification title, message, and visibility
              </p>
              <span className="cms-modal__mode-badge">Edit Mode</span>
            </div>
          </div>
        </Modal.Header>

        <form onSubmit={handleSubmit(onSubmit)}>
          <Modal.Body className="cms-modal__body">
            <div className="cms-settings-panel">
              <div className="cms-settings-panel__col">
                <span className="cms-settings-label">Template</span>
                <div className="cms-form-block mb-0" style={{ padding: "0.85rem 1rem" }}>
                  <p className="fw-bold mb-0 text-dark" style={{ fontSize: "0.92rem" }}>
                    {selectedTemplate?.title || "—"}
                  </p>
                  {templateKey && (
                    <span className="nt-template-key">
                      <i className="fas fa-key" />
                      {templateKey}
                    </span>
                  )}
                </div>
              </div>

              <div className="cms-settings-panel__divider" />

              <div className="cms-settings-panel__col cms-settings-panel__col--status">
                <span className="cms-settings-label">Publication</span>
                <div className={`cms-status-row ${statusActive ? "is-active" : ""}`}>
                  <div className="cms-status-row__text">
                    <strong>{statusActive ? "Active" : "Inactive"}</strong>
                    <span>
                      {statusActive
                        ? "Template will send notifications"
                        : "Template is disabled"}
                    </span>
                  </div>
                  <div className="cms-switch-wrap">
                    <span className="cms-switch__label">{statusActive ? "ON" : "OFF"}</span>
                    <label className="cms-switch">
                      <input
                        type="checkbox"
                        checked={statusActive}
                        onChange={(e) =>
                          setValue("status", e.target.checked ? "active" : "inactive", {
                            shouldValidate: true,
                          })
                        }
                      />
                      <span className="cms-switch__slider" />
                    </label>
                  </div>
                </div>
                <input type="hidden" {...register("status", { required: "Status is required" })} />
              </div>
            </div>

            <div className="cms-content-block">
              <div className="cms-content-block__head">
                <h5 className="cms-content-block__title">
                  <i className="fas fa-comment-dots" />
                  Message Content
                </h5>
                <span className="cms-content-block__hint">Title &amp; body text</span>
              </div>
              <div className="cms-content-block__body">
                <div>
                  <label className="cms-field-label">
                    Template Title <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className={`cms-input ${errors.title ? "is-invalid" : ""}`}
                    placeholder="e.g. Use {{promoCode}} now!"
                    {...register("title", { required: "Title is required" })}
                  />
                  {errors.title && (
                    <span className="cms-field-error">{errors.title.message}</span>
                  )}
                </div>

                <div>
                  <label className="cms-field-label">
                    Message Template <span className="text-danger">*</span>
                  </label>
                  <textarea
                    className={`cms-textarea ${errors.description ? "is-invalid" : ""}`}
                    rows={5}
                    placeholder="e.g. Get {{discountValue}}% off. Min order {{minOrderValue}}."
                    {...register("description", { required: "Description is required" })}
                  />
                  {errors.description && (
                    <span className="cms-field-error">{errors.description.message}</span>
                  )}

                  <div className="nt-placeholder-panel">
                    <span className="nt-placeholder-panel__label">
                      <i className="fas fa-info-circle" />
                      Available placeholders — tap to copy style
                    </span>
                    <div className="nt-placeholder-chips">
                      {PLACEHOLDERS.map((ph) => (
                        <span key={ph} className="nt-placeholder-chip">
                          {ph}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Modal.Body>

          <div className="cms-modal-footer">
            <button type="button" className="cms-btn-cancel" onClick={closeModal} disabled={saving}>
              Cancel
            </button>
            <button type="submit" className="cms-btn-save" disabled={saving || loadingTemplate}>
              {saving ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" />
                  Saving...
                </>
              ) : (
                <>
                  <i className="fas fa-check me-2" />
                  Save Changes
                </>
              )}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
